import React, {useEffect, useRef, useState} from 'react'

import {ImagePlaceholder} from '@vio/atlas/helpers/ImagePlaceholder'
import {Image} from '@vio/atlas/src/components/designSystem/Image'
import {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {
  GalleryImage,
  Root,
  Scroller,
  Slide,
  SlideSkeleton,
  Viewport
} from './styles'
import {SwipeGalleryDots} from './SwipeGalleryDots'

interface Props {
  hotelId: string
  images: string[]
  isPriority: boolean
  className?: string
  hideDots?: boolean
  imageHeight?: number
  imageProvider: ImageProvider
  onClick?: () => void
  onSlideChange?: (index: number) => void
}

const SwipeImageGalleryComponent: React.FC<Props> = ({
  hotelId,
  images,
  className,
  isPriority,
  hideDots = false,
  imageHeight,
  imageProvider,
  onClick,
  onSlideChange
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const isHydrated = useIsHydrated()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]))

  // Track which slide is currently visible using IntersectionObserver
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!scrollerRef.current) return
    if (!images?.length) return

    const observer = new IntersectionObserver(
      entries => {
        // Find the entry with the highest intersection ratio
        let mostVisibleEntry = null
        let highestRatio = 0

        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
            highestRatio = entry.intersectionRatio
            mostVisibleEntry = entry
          }
        })

        if (mostVisibleEntry && mostVisibleEntry.intersectionRatio > 0.5) {
          const slideId = mostVisibleEntry.target.id
          const slideIndex = Number(slideId.replace(`${hotelId}-slide-`, ''))
          const nextSlideIndex = slideIndex + 1
          if (!isNaN(slideIndex)) {
            setCurrentIndex(slideIndex)
            onSlideChange?.(slideIndex)

            // Mark this image and adjacent images as should be loaded
            setLoadedImages(prev => {
              const loaded = new Set(prev)
              loaded.add(slideIndex)

              if (slideIndex !== 0 && nextSlideIndex < images.length) {
                loaded.add(nextSlideIndex)
              }
              return loaded
            })
          }
        }
      },
      {
        root: scrollerRef.current,
        threshold: [0, 0.5, 1]
      }
    )

    const slides = slideRefs.current
    slides.forEach(slide => {
      if (slide) observer.observe(slide)
    })

    return () => {
      slides.forEach(slide => {
        if (slide) observer.unobserve(slide)
      })
    }
  }, [images.length, onSlideChange, hotelId])

  useEffect(() => {
    if (isHydrated) {
      setLoadedImages(prev => {
        const loaded = new Set(prev)
        if (loaded.has(0) && loaded.size === 1) {
          loaded.add(1)
        }
        return loaded
      })
    }
  }, [isHydrated])

  if (!images?.length) return null

  return (
    <Root className={className}>
      <Viewport onClick={onClick}>
        <Scroller ref={scrollerRef}>
          {images.map((img, index) => (
            <Slide
              id={`${hotelId}-slide-${index}`}
              key={index}
              ref={el => {
                slideRefs.current[index] = el
              }}
            >
              <GalleryImage imageHeight={imageHeight}>
                <Image
                  url={loadedImages.has(index) ? img : ''}
                  imageProvider={imageProvider}
                  size="mobileLarge"
                  isResponsive
                  placeHolder={
                    loadedImages.has(index) ? (
                      <ImagePlaceholder />
                    ) : (
                      <SlideSkeleton height="100%" />
                    )
                  }
                  loading={isPriority && index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={isPriority && index === 0 ? 'high' : 'low'}
                  data-id={`${hotelId}-SwipeImageGalleryImage-${index}`}
                />
              </GalleryImage>
            </Slide>
          ))}
        </Scroller>
      </Viewport>
      {isHydrated && !hideDots && (
        <SwipeGalleryDots
          hotelId={hotelId}
          totalImages={images.length}
          currentIndex={currentIndex}
        />
      )}
    </Root>
  )
}

export const SwipeImageGallery = React.memo(SwipeImageGalleryComponent)
